// src/store/slices/admin/blogPostSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getBlogPosts = createAsyncThunk(
    'blogPosts/getBlogPosts',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.BLOG_POSTS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Blog posts retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch blog posts', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch blog posts';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const getBlogPost = createAsyncThunk(
    'blogPosts/getBlogPost',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.BLOG_POST(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Blog post retrieved', post: data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch blog post', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch blog post';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createBlogPost = createAsyncThunk(
    'blogPosts/createBlogPost',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            // For file upload, use FormData
            let formData = data;
            if (data instanceof FormData) {
                // Already FormData
            } else {
                // Convert to FormData if needed (for image)
                // We'll assume the caller sends FormData when file is included
                // But we'll send JSON if no file
                // For simplicity, we'll send as is (assuming JSON)
            }
            const response = await apiClient.post(API_ENDPOINTS.BLOG_POSTS + '/create', data, {
                headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
            });
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Blog post created', post: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create blog post', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create blog post';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updateBlogPost = createAsyncThunk(
    'blogPosts/updateBlogPost',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.BLOG_POST(id), data, {
                headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
            });
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Blog post updated', post: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update blog post', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update blog post';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deleteBlogPost = createAsyncThunk(
    'blogPosts/deleteBlogPost',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.BLOG_POST(id));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Blog post deleted', id };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete blog post', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete blog post';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const toggleBlogPostStatus = createAsyncThunk(
    'blogPosts/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.patch(API_ENDPOINTS.BLOG_POST_TOGGLE(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Status toggled', id, is_active: data?.is_active };
            } else {
                return rejectWithValue({ message: message || 'Failed to toggle status', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle status';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const exportBlogPosts = createAsyncThunk(
    'blogPosts/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.BLOG_POSTS_EXPORT, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Exported successfully', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to export', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to export';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [],
    pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
    currentItem: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const blogPostSlice = createSlice({
    name: 'blogPosts',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentItem = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
        clearCurrentItem: (state) => { state.currentItem = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getBlogPosts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBlogPosts.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData.data || [];
                state.pagination = responseData.meta || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
                state.successMessage = payload.message || 'Blog posts retrieved';
                state.apiMessage = payload.message || 'Blog posts retrieved';
                state.error = null;
            })
            .addCase(getBlogPosts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch blog posts';
                state.apiMessage = action.payload?.message || 'Failed to fetch blog posts';
                state.successMessage = null;
                state.items = [];
                state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            })
            .addCase(getBlogPost.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBlogPost.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentItem = action.payload.post || null;
                state.successMessage = action.payload.message || 'Blog post retrieved';
                state.apiMessage = action.payload.message || 'Blog post retrieved';
                state.error = null;
            })
            .addCase(getBlogPost.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch blog post';
                state.apiMessage = action.payload?.message || 'Failed to fetch blog post';
                state.successMessage = null;
                state.currentItem = null;
            })
            .addCase(createBlogPost.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createBlogPost.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const newItem = action.payload.post;
                if (newItem) {
                    state.items = [newItem, ...state.items];
                }
                state.successMessage = action.payload.message || 'Blog post created';
                state.apiMessage = action.payload.message || 'Blog post created';
                state.error = null;
            })
            .addCase(createBlogPost.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create blog post';
                state.apiMessage = action.payload?.message || 'Failed to create blog post';
                state.successMessage = null;
            })
            .addCase(updateBlogPost.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateBlogPost.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const updated = action.payload.post;
                if (updated) {
                    state.items = state.items.map(item => item.id === updated.id ? updated : item);
                    if (state.currentItem?.id === updated.id) {
                        state.currentItem = updated;
                    }
                }
                state.successMessage = action.payload.message || 'Blog post updated';
                state.apiMessage = action.payload.message || 'Blog post updated';
                state.error = null;
            })
            .addCase(updateBlogPost.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update blog post';
                state.apiMessage = action.payload?.message || 'Failed to update blog post';
                state.successMessage = null;
            })
            .addCase(deleteBlogPost.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteBlogPost.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const id = action.payload.id;
                state.items = state.items.filter(item => item.id !== id);
                if (state.currentItem?.id === id) state.currentItem = null;
                state.successMessage = action.payload.message || 'Blog post deleted';
                state.apiMessage = action.payload.message || 'Blog post deleted';
                state.error = null;
            })
            .addCase(deleteBlogPost.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete blog post';
                state.apiMessage = action.payload?.message || 'Failed to delete blog post';
                state.successMessage = null;
            })
            .addCase(toggleBlogPostStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleBlogPostStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, is_active } = action.payload;
                state.items = state.items.map(item => item.id === id ? { ...item, is_active } : item);
                if (state.currentItem?.id === id) state.currentItem = { ...state.currentItem, is_active };
                state.successMessage = action.payload.message || 'Status toggled';
                state.apiMessage = action.payload.message || 'Status toggled';
                state.error = null;
            })
            .addCase(toggleBlogPostStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle status';
                state.apiMessage = action.payload?.message || 'Failed to toggle status';
                state.successMessage = null;
            })
            .addCase(exportBlogPosts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportBlogPosts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Exported successfully';
                state.apiMessage = action.payload.message || 'Exported successfully';
                state.error = null;
            })
            .addCase(exportBlogPosts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export';
                state.apiMessage = action.payload?.message || 'Failed to export';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
    clearCurrentItem,
} = blogPostSlice.actions;

export const selectBlogPosts = (state) => state.blogPosts?.items || [];
export const selectBlogPostPagination = (state) => state.blogPosts?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentBlogPost = (state) => state.blogPosts?.currentItem || null;
export const selectBlogPostsLoading = (state) => state.blogPosts?.isLoading || false;
export const selectBlogPostsSubmitting = (state) => state.blogPosts?.isSubmitting || false;
export const selectBlogPostsError = (state) => state.blogPosts?.error || null;
export const selectBlogPostsApiMessage = (state) => state.blogPosts?.apiMessage || null;

export default blogPostSlice.reducer;